import { PageMeta } from "@/components/PageMeta";
import { PolicyLayout, type PolicySection } from "@/components/PolicyLayout";
import { siteConfig } from "@/config/site";
import { routeSeo } from "@/content/seo";

const sections: PolicySection[] = [
  { id: "current-status", label: "1. Current payment status" },
  { id: "when-applicable", label: "2. When this policy applies" },
  { id: "duplicate-charges", label: "3. Duplicate and erroneous charges" },
  { id: "statutory-cancellation", label: "4. Statutory cancellation rights (UK consumers)" },
  { id: "discretionary-refunds", label: "5. Discretionary refunds (outside the 14-day period)" },
  { id: "technical-failure", label: "6. Technical failure or non-delivery" },
  { id: "business-customers", label: "7. Business customers" },
  { id: "how-to-request", label: "8. How to request a refund" },
  { id: "processing", label: "9. Processing expectations" },
  { id: "relationship-to-cancellation", label: "10. Relationship to cancellation" },
  { id: "disputes", label: "11. Disputes and chargebacks" },
  { id: "enterprise", label: "12. Enterprise agreements" },
  { id: "contact", label: "13. Contact" },
];

export function RefundsPage() {
  const seo = routeSeo["/legal/refunds"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/legal/refunds" />
      <PolicyLayout
        slug="refunds"
        title="Refund Policy"
        intro="This policy explains how refunds work for paid Devory subscriptions."
        sections={sections}
      >
        <h2 id="current-status">1. Current payment status</h2>
        <p>
          Devory Cloud offers real, live paid subscriptions (Builder, Professional, Studio and
          Team), purchased and billed through Stripe from inside Devory Cloud (Settings &gt; Billing).
          This marketing website does not itself embed a checkout form.
        </p>

        <h2 id="when-applicable">2. When this policy applies</h2>
        <p>
          This policy applies to any payment made for a Devory subscription, processed through
          Stripe.
        </p>

        <h2 id="duplicate-charges">3. Duplicate and erroneous charges</h2>
        <p>
          If you are charged twice for the same subscription period, or charged in error due to a
          billing system fault, Devory will refund the duplicate or erroneous amount once verified.
        </p>

        <h2 id="statutory-cancellation">4. Statutory cancellation rights (UK consumers)</h2>
        <p>
          <em>
            This section explains Devory's V1 refund approach. It does not limit any statutory
            rights you may have as a consumer.
          </em>
        </p>
        <p>
          If you are contracting as a consumer in the UK, you have a statutory right under the
          Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013 to
          cancel your subscription within 14 days of first subscribing, without giving a reason.
        </p>
        <p>
          When you subscribe, Devory's checkout requires you to expressly confirm that you'd like
          your Devory access to begin immediately, rather than waiting until the 14-day period ends.
          If you cancel within the 14-day period after making that request, you will be refunded the
          amount paid minus a reasonable, proportionate amount for the time your subscription was
          actually active -- calculated by the number of days that had elapsed compared to the
          length of your billing period, not by how much of your plan's workspace or member capacity
          you used. This reflects that Devory's paid tiers are priced for a period of access, not for
          metered consumption, so using your plan's capacity heavily within the 14 days does not
          reduce your refund.
        </p>
        <p>
          This statutory right exists in addition to, and is not replaced by, the discretionary
          policy in section 5 below. Nothing in this policy is intended to remove any right you have
          under UK consumer law that cannot be excluded by agreement.
        </p>

        <h2 id="discretionary-refunds">5. Discretionary refunds (outside the 14-day period)</h2>
        <p>
          Outside of duplicate or erroneous charges and the statutory 14-day right above, refunds for
          partially used subscription periods will be considered at Devory's discretion. Devory does
          not guarantee a fixed refund window beyond what section 4 describes; any further standard
          refund window Devory adopts will be published here before it takes effect.
        </p>

        <h2 id="technical-failure">6. Technical failure or non-delivery</h2>
        <p>
          If a technical failure on Devory's side genuinely prevents you from using the service you
          paid for, Devory will work to restore access as quickly as reasonably possible. Where
          restoration isn't possible or doesn't fairly resolve the affected period, Devory will
          credit or refund the affected time rather than require you to pay for service you could not
          use.
        </p>

        <h2 id="business-customers">7. Business customers</h2>
        <p>
          The statutory cancellation right in section 4 applies to consumers only. If you are
          contracting in the course of a business, trade, or profession, that statutory right does
          not apply to you, and refunds for your subscription are governed by section 5 and any
          separate written agreement in place, rather than by section 4.
        </p>

        <h2 id="how-to-request">8. How to request a refund</h2>
        <p>
          Submit refund requests through the{" "}
          <a href="/contact">contact form</a> (Billing/Support category) or by emailing{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>, including
          your account details and the reason for the request.
        </p>

        <h2 id="processing">9. Processing expectations</h2>
        <p>
          Devory processes verified refunds as promptly as reasonably possible. Devory does not
          commit to a specific number of business days until that process is defined and published
          here.
        </p>

        <h2 id="relationship-to-cancellation">10. Relationship to cancellation</h2>
        <p>
          Refunds and cancellations are related but distinct: see the{" "}
          <a href="/legal/cancellation">Cancellation Policy</a> for how ending a subscription
          works, separate from whether any amount already paid is refunded. Cancelling future renewal
          is not the same action as requesting a refund of the current paid period.
        </p>

        <h2 id="disputes">11. Disputes and chargebacks</h2>
        <p>
          If you believe a charge is incorrect, Devory asks that you contact Devory directly first
          so the issue can be resolved quickly, before initiating a dispute with your card issuer or
          payment provider.
        </p>

        <h2 id="enterprise">12. Enterprise agreements</h2>
        <p>
          Where a signed enterprise agreement includes its own refund terms, those terms take
          precedence over this public policy for that customer.
        </p>

        <h2 id="contact">13. Contact</h2>
        <p>
          Questions about this policy can be sent to{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>.
        </p>
      </PolicyLayout>
    </>
  );
}
